import { IconContentEdit } from '@doltech/icons/IconContentEdit';
import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import { isNotEmpty } from '@doltech/utils/lib/text';
import { Input, Popover, Space } from 'antd';
import { get, noop } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';
import { Grid } from 'styled-css-grid';

import { colorsV2 } from '../../../../../colors-v2';
import { Typography } from '../../../../../DesignSystemV2/Typography';
import { showToast } from '../../../../../Toast';
import { useFileUploadStore } from '../../model/file-upload-model';

const Wrapper = styled(Grid)`
  width: 528px;
  padding: 20px 16px;
  background: ${colorsV2.white100};
  box-shadow: 0 16px 24px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.04),
    0 0 1px rgba(0, 0, 0, 0.04);
  border-radius: 12px;
`;

interface Props {
  id?: string;
  onUpdateAlt?: (value: string) => void;
  alt?: string;
  children?: JSX.Element;
}

const StyledRemove = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${colorsV2.gray10};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

export const InputAltComponentPopOver = observer(
  ({ id = undefined, onUpdateAlt = noop, alt = undefined, children }: Props) => {
    const [visible, setVisible] = React.useState(false);

    const fileUploadStore = useFileUploadStore();
    const imageAlt = fileUploadStore?.getFileById(id)?.alt || alt || undefined;
    const ref = React.useRef<any>();

    const handleVisibleChange = (data) => {
      setVisible(data);
      if (!data) {
        if (!id) {
          onUpdateAlt(ref.current?.resizableTextArea?.props?.value);
        } else {
          fileUploadStore.updateAlt(id, ref.current?.resizableTextArea?.props?.value);
        }
      }
      const inputValue = get(ref.current, 'resizableTextArea.props.value') || undefined;
      if (inputValue !== imageAlt) {
        showToast({
          icon: 'success',
          title: 'Update image alt success',
          subTitle: 'Update image alt success',
        });
      }
    };
    return (
      <Popover
        trigger="click"
        visible={visible}
        onVisibleChange={(item) => {
          handleVisibleChange(item);
        }}
        overlayClassName="dol-popover"
        content={
          <Wrapper columns="minmax(0, 1fr)" rows="max-content auto">
            <Grid rows={1} columns="auto max-content">
              <Typography.Text variant="semi-bold/16">Alt Text</Typography.Text>
              <StyledRemove onClick={() => handleVisibleChange(false)}>
                <IconOtherClose fontSize={20} />
              </StyledRemove>
            </Grid>
            <Input.TextArea
              ref={ref}
              defaultValue={imageAlt}
              placeholder="Type question"
              className="dol-input"
              autoSize={{ minRows: 3, maxRows: 6 }}
              style={{ resize: 'none' }}
            />
          </Wrapper>
        }
      >
        <div aria-hidden onClick={() => setVisible(true)}>
          {children}
        </div>
      </Popover>
    );
  }
);

export const InputAltComponent = observer((props: Props) => {
  const { id, alt } = props;
  const fileUploadStore = useFileUploadStore();

  const imageAlt = fileUploadStore?.getFileById(id)?.alt || alt || undefined;

  return (
    <InputAltComponentPopOver {...props}>
      <Space align="start" direction="horizontal" size={12}>
        <IconContentEdit fontSize={20} color={colorsV2.gray100} />
        <Typography.Text className="font-inter" variant="semi-bold/16" color="gray100">
          {isNotEmpty(imageAlt) ? 'Edit Alt Text' : 'Input Alt Text'}
        </Typography.Text>
      </Space>
    </InputAltComponentPopOver>
  );
});
